import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, DownloadIcon, FileSpreadsheetIcon, ShieldIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Insurance as InsuranceType } from '../../types';
import { exportInsurancesToExcel } from '../../utils/excelExport';
import { exportInsurancesToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { useConfirm } from '../../contexts/ConfirmContext';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Insurance() {
  const {
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const {
    confirm
  } = useConfirm();
  const readOnly = isReadOnly('insurance');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceType | null>(null);
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    coverageType: '',
    phone: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-insurance-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateInsurance(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateInsurance(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateInsurance(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const filteredInsurances = useMemo(() => insurances.filter(i => {
    const matchesSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.company.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(i.id);
    return matchesSearch && status === viewMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [insurances, searchTerm, viewMode, itemStatuses]);
  const {
    sortedItems: sortedInsurances,
    requestSort,
    isSorted
  } = useSortableData(filteredInsurances);
  const {
    paginatedItems: paginatedInsurances,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedInsurances);
  // Counts for tabs
  const activeCount = insurances.filter(i => getItemStatus(i.id) === 'active').length;
  const archivedCount = insurances.filter(i => getItemStatus(i.id) === 'archived').length;
  const deactivatedCount = insurances.filter(i => getItemStatus(i.id) === 'deactivated').length;
  const openNewForm = () => {
    if (!canCreate('insurance')) return;
    setSelectedInsurance(null);
    setFormData({
      name: '',
      company: '',
      coverageType: '',
      phone: '',
      email: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (insurance: InsuranceType) => {
    if (!canEdit('insurance')) return;
    setSelectedInsurance(insurance);
    setFormData({
      name: insurance.name,
      company: insurance.company,
      coverageType: insurance.coverageType,
      phone: insurance.phone,
      email: insurance.email
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openActionConfirm = (insurance: InsuranceType, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedInsurance(insurance);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Seguro';
      message = `¿Estás seguro de que deseas archivar a "${insurance.name}"? Podrás restaurarlo más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Seguro';
      message = `¿Estás seguro de que deseas desactivar a "${insurance.name}"? No aparecerá en las listas activas.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Seguro';
      message = `¿Estás seguro de que deseas eliminar permanentemente a "${insurance.name}"? Esta acción no se puede deshacer.`;
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          archiveItem(insurance.id);
          showToast({
            type: 'info',
            title: 'Seguro archivado',
            message: `${insurance.name} fue archivado.`
          });
        } else if (type === 'deactivate') {
          deactivateItem(insurance.id);
          showToast({
            type: 'warning',
            title: 'Seguro desactivado',
            message: `${insurance.name} fue desactivado.`
          });
        } else if (type === 'delete') {
          deleteInsurance(insurance.id);
          showToast({
            type: 'error',
            title: 'Seguro eliminado',
            message: `${insurance.name} fue eliminado permanentemente.`
          });
        }
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.company) errors.company = 'La compañía es requerida';
    if (!formData.coverageType) errors.coverageType = 'El tipo de cobertura es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: selectedInsurance ? 'Guardar Cambios' : 'Crear Seguro',
      message: selectedInsurance ? '¿Estás seguro de que deseas guardar los cambios de este seguro?' : '¿Estás seguro de que deseas registrar este nuevo seguro?',
      confirmText: 'Guardar',
      onConfirm: () => {
        if (selectedInsurance) {
          updateInsurance(selectedInsurance.id, formData);
          showToast({
            type: 'success',
            title: 'Seguro actualizado',
            message: `${formData.name} fue actualizado correctamente.`
          });
        } else {
          addInsurance(formData);
          showToast({
            type: 'success',
            title: 'Seguro creado',
            message: `${formData.name} fue registrado correctamente.`
          });
        }
        setIsFormOpen(false);
      }
    });
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToExcel(insurances);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToPdf(insurances);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-3782" data-mp="2igc4exlqn3cr">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-xl text-white" data-id="element-3783" data-mp="2newysgeb6sta">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 23px, rgba(255,255,255,1) 23px, rgba(255,255,255,1) 24px)'
      }} data-id="element-3784" data-mp="1uppwr7fnbl3a" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-3785" data-mp="3b4r8ip3c019b" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-3786" data-mp="3g5f83z5mlqf5" />
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-3787" data-mp="14f5yf2kz5joz" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-3788" data-mp="pq1ixt5kdtkk" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-3789" data-mp="br1sqh2el6ma" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3790" data-mp="6s706nemw3oz">
          <div data-id="element-3791" data-mp="265umeajq7nbl">
            <div className="flex items-center gap-3 mb-2" data-id="element-3792" data-mp="2649tsqgz2egd">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-3793" data-mp="21688rzzzk529">
                <ShieldIcon className="w-6 h-6 text-white" data-id="element-3794" data-mp="3vmgca507z7ra" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-3795" data-mp="3vkdirazuywht">Seguros</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-3796" data-mp="493puveq8ts1">
              Gestión de seguros médicos y coberturas
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3797" data-mp="3qmkdck6dq6il">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3798" data-mp="n76e37zgamos">
            {canExportPdf && <Button variant="outline" leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-3800" data-mp="8bgrp8qcs7jj" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3799" data-mp="89mg4l0z1rs4">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3802" data-mp="1g35y4bioxnw7" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3801" data-mp="1g0t5n4fb3v4w">
                Exportar Excel
              </Button>}
            {canCreate('insurance') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3804" data-mp="xbd6akfcy8q2" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-3803" data-mp="xa21cha6euxd">
                Nuevo Seguro
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-3805" data-mp="11a2dtwdpsw1s">
          <LockIcon className="w-4 h-4" data-id="element-3806" data-mp="9kqvwmhvsccw" />
          <span data-id="element-3807" data-mp="mai52h8v6swd">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            seguros.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-3808" data-mp="w8ebwwurr1oo">
        <div className="w-full md:w-96 relative" data-id="element-3809" data-mp="w6bdbofjw08c">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-3810" data-mp="10zebyaz6pzve" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar seguros..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-3811" data-mp="15xo4sexfsodu" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3812" data-mp="3gr247t39747v">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3813" data-mp="wy8wc7x9a3w">
              <tab.icon className="w-4 h-4" data-id="element-3814" data-mp="26rratypvfkd6" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-3815" data-mp="26ulsjihi2r2w">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Insurance List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-3816" data-mp="ov5sfxcgbgvi">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-3817" data-mp="ttqorlol1geo" />
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3818" data-mp="f52x3qhuxqu0">
          {filteredInsurances.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-3819" data-mp="jwkxacc23ndx">
              <p className="text-sm" data-id="element-3820" data-mp="64xqhikhmwgw">No se encontraron seguros</p>
            </div> : paginatedInsurances.map(insurance => <div key={insurance.id} className="px-4 py-3 flex items-center gap-3" data-id="element-3821" data-mp="b151hprqqy5p">
                <div className="w-9 h-9 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 flex-shrink-0" data-id="element-3822" data-mp="15l57299lxgf">
                  <ShieldIcon className="w-4 h-4" data-id="element-3823" data-mp="3dqql2hm4cm0i" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-3824" data-mp="39on63j08qzah">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3825" data-mp="3nmk5en62xjsu">
                    {insurance.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3826" data-mp="3im5xd54ha9ut">
                    {insurance.company} · {insurance.coverageType}
                  </p>
                  {(insurance.phone || insurance.email) && <p className="text-xs text-gray-400 truncate" data-id="element-3827" data-mp="a7xjl36m7nmp">
                      {insurance.phone || insurance.email}
                    </p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3828" data-mp="5b6uxrgqvjpo">
                  {viewMode === 'active' && canEdit('insurance') && <button onClick={() => openEditForm(insurance)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" data-id="element-3829" data-mp="f835e2q7efwv">
                      <EditIcon className="w-4 h-4" data-id="element-3830" data-mp="310zq5k0rivh5" />
                    </button>}
                  {viewMode === 'active' && canDelete('insurance') && <button onClick={() => openActionConfirm(insurance, 'archive')} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-3831" data-mp="3awkzk385uy0x">
                      <ArchiveIcon className="w-4 h-4" data-id="element-3832" data-mp="2n78gmbn17blf" />
                    </button>}
                  {viewMode !== 'active' && canDelete('insurance') && <button onClick={() => restoreItem(insurance.id)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" data-id="element-3833" data-mp="1uj48ljgzmmfk">
                      <RotateCcwIcon className="w-4 h-4" data-id="element-3834" data-mp="gm1mfa0uagg1" />
                    </button>}
                </div>
              </div>)}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-3835" data-mp="7kto4rybpfly">
          <table className="w-full" data-id="element-3836" data-mp="2ntigi5dyevd">
            <thead data-id="element-3837" data-mp="2ms68acmvmc2">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3838" data-mp="2peds9ziy1ua">
                <SortableHeader label="Nombre" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-3839" data-mp="3tvfuay29dnnb" />

                <SortableHeader label="Compañía" sortKey="company" currentDirection={isSorted('company')} onSort={requestSort} data-id="element-3840" data-mp="3g26ot8hflma2" />

                <SortableHeader label="Cobertura" sortKey="coverageType" currentDirection={isSorted('coverageType')} onSort={requestSort} data-id="element-3841" data-mp="363iih6yt6vm7" />

                <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} data-id="element-3842" data-mp="2zxvxaoeo12mg" />

                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3843" data-mp="2qvmpi6as7ncr">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3844" data-mp="2h49x9dl2mv67">
              {filteredInsurances.length === 0 ? <tr data-id="element-3845" data-mp="278f9y55wkbx3">
                  <td colSpan={5} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-3846" data-mp="278yg6om6xbva">
                    <div className="flex flex-col items-center justify-center" data-id="element-3847" data-mp="1y78p5jw80ey1">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3848" data-mp="1y9boiuj9jkff">
                        <ShieldIcon className="w-8 h-8 text-gray-400" data-id="element-3849" data-mp="io15kw7xs2wx" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3850" data-mp="mq4i7o6cddjs">
                        No se encontraron seguros
                      </h3>
                      <p className="text-sm" data-id="element-3851" data-mp="rmlfp8zfl49j">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo seguro' : 'No hay seguros en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedInsurances.map(insurance => <tr key={insurance.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-3852" data-mp="37e98cs7ewamj">
                    <td className="py-4 px-6" data-id="element-3853" data-mp="jivd4fpg4jjk">
                      <div className="flex items-center gap-3" data-id="element-3854" data-mp="em52i7weug5j">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-3855" data-mp="ejskxvuke7t0">
                          <ShieldIcon className="w-4 h-4" data-id="element-3856" data-mp="ocpv9epekqgj" />
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-3857" data-mp="mvjl0gtl7xv">
                          {insurance.name}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-gray-100" data-id="element-3858" data-mp="9nlgk7hth4d6">
                      {insurance.company}
                    </td>
                    <td className="py-4 px-6" data-id="element-3859" data-mp="4ps817t1jy9q">
                      <Badge variant="info" className="bg-teal-50 text-teal-700 border border-teal-100" data-id="element-3860" data-mp="1l0nwr1g07rwt">
                        {insurance.coverageType}
                      </Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-3861" data-mp="1slq2nh5esug5">
                      <div className="space-y-1" data-id="element-3862" data-mp="1smjcws3ku231">
                        {insurance.phone && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-id="element-3863" data-mp="1en9sthc92nts">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3864" data-mp="1jmmisayf4b0l" />{' '}
                            {insurance.phone}
                          </p>}
                        {insurance.email && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-id="element-3865" data-mp="1xkkvzd1061ei">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3866" data-mp="22imbjnz7zil8" />{' '}
                            {insurance.email}
                          </p>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-3867" data-mp="2v19q0kjpnzdc">
                      <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-3868" data-mp="16dmabstrbr7o">
                        {viewMode === 'active' ? <>
                            {canEdit('insurance') && <button onClick={() => openEditForm(insurance)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3869" data-mp="se32l14uyhvw">
                                <EditIcon className="w-4 h-4" data-id="element-3870" data-mp="1gb0fgwa8ctko" />
                              </button>}
                            {canDelete('insurance') && <>
                                <button onClick={() => openActionConfirm(insurance, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3871" data-mp="28tnv3w6gjged">
                                  <ArchiveIcon className="w-4 h-4" data-id="element-3872" data-mp="2qakz8fckkmll" />
                                </button>
                                <button onClick={() => openActionConfirm(insurance, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3873" data-mp="2ucexkgzaleil">
                                  <PowerOffIcon className="w-4 h-4" data-id="element-3874" data-mp="3e1gvboo263yn" />
                                </button>
                              </>}
                          </> : canDelete('insurance') && <button onClick={() => restoreItem(insurance.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3875" data-mp="1dyxxok3jw6d6">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-3876" data-mp="1wnuqskn7ostt" />
                            </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-3877" data-mp="gansjw8gx2hu">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3878" data-mp="c8st6xa3kz6p" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedInsurance ? 'Editar Seguro' : 'Nuevo Seguro'} size="md" icon={<ShieldIcon className="w-5 h-5" data-id="element-3880" data-mp="3ek66pyyegwow" />} accentColor="sky" data-id="element-3879" data-mp="3ohuf3roxi2pj">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3881" data-mp="3jldgg3k3s5y7">
          {/* Insurance Info */}
          <div data-id="element-3882" data-mp="35luke94kgl29">
            <div className="flex items-center gap-2 mb-4" data-id="element-3883" data-mp="35mdtd0c8nz45">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-3884" data-mp="3ajdyn919kxu7"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3885" data-mp="13tg5zk18dyql">
                Información del Seguro
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3886" data-mp="18qgahqifmtfl">
              <Input label="Nombre del Seguro" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: ARS Humano" data-id="element-3887" data-mp="2ce5307kgcm5c" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3888" data-mp="1tndo6qhp43jk">
                <Input label="Compañía" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} error={formErrors.company} placeholder="Ej: Humano Seguros" data-id="element-3889" data-mp="1tm2n4yfmx9w5" />

                <Input label="Tipo de Cobertura" value={formData.coverageType} onChange={e => setFormData({
                ...formData,
                coverageType: e.target.value
              })} error={formErrors.coverageType} placeholder="Ej: Completo, Básico" data-id="element-3890" data-mp="3eym53ypydr6h" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div data-id="element-3891" data-mp="hwfy5bo185p2">
            <div className="flex items-center gap-2 mb-4" data-id="element-3892" data-mp="mswn28322o37">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-3893" data-mp="mtg4zosphch8"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3894" data-mp="qu04v04nyu72">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3895" data-mp="22agbc3y8q0hs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3896" data-mp="277q864nylefn">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-555-0000" data-id="element-3897" data-mp="2yab28h8f7pmj" />

                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="info@seguro.com" data-id="element-3898" data-mp="2ti1b4xvkkjio" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-3899" data-mp="3vbtcitbcb33">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3900" data-mp="8tdcf3qqoaek">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3901" data-mp="1oe5shmkwefg6">
              {selectedInsurance ? 'Guardar Cambios' : 'Crear Seguro'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Seguros" isExporting={isExporting} data-id="element-3902" data-mp="1ya8xr1b1qu7c" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Seguros a PDF" isExporting={isPdfExporting} data-id="element-3903" data-mp="2m0bjz0sa55y1" />
    </div>;
}